import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';
import { 
  Mail, 
  Send, 
  Loader2, 
  ChevronDown, 
  ChevronUp, 
  RefreshCw,
  AlertCircle,
  ExternalLink,
  MessageSquare,
  Clock
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

function EmailThread({ thread, onSelect, isSelected }) {
  const dateStr = thread.date ? new Date(thread.date).toLocaleDateString() : '';
  
  return (
    <div 
      className={`p-4 border-b border-[#E5E7EB] cursor-pointer hover:bg-gray-50 transition-colors ${
        isSelected ? 'bg-[#FDF8E8]' : ''
      } ${thread.is_unread ? 'bg-blue-50' : ''}`}
      onClick={() => onSelect(thread)}
      data-testid={`email-thread-${thread.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {thread.is_unread && (
              <span className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0" />
            )}
            <p className={`text-sm truncate ${thread.is_unread ? 'font-semibold' : 'font-medium'} text-[#1A1A1A]`}>
              {thread.subject || '(No subject)'}
            </p>
          </div>
          <p className="text-xs text-[#6B7280] mt-1 truncate">{thread.snippet}</p>
        </div>
        <div className="flex-shrink-0 text-right">
          <p className="text-xs text-[#9CA3AF]">{dateStr}</p>
          <div className="flex items-center gap-1 mt-1 text-xs text-[#6B7280]">
            <MessageSquare className="w-3 h-3" />
            {thread.message_count}
          </div>
        </div>
      </div>
    </div>
  );
}

function ThreadDetail({ thread, onClose, onRefresh, contactId }) {
  const { token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchThread();
  }, [thread.id]);

  const fetchThread = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/gmail/thread/${thread.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setMessages(data.messages || []);
    } catch (err) {
      console.error('Error fetching thread:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyBody.trim()) return;
    
    setSending(true);
    try {
      const lastMessage = messages[messages.length - 1];
      const res = await fetch(`${API}/api/gmail/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          to: lastMessage?.from?.includes('<') 
            ? lastMessage.from.match(/<(.+)>/)?.[1] || lastMessage.from 
            : lastMessage.from,
          subject: `Re: ${thread.subject || ''}`,
          body: replyBody,
          thread_id: thread.id,
          contact_id: contactId
        })
      });
      
      if (res.ok) {
        setReplyBody('');
        setReplyOpen(false);
        fetchThread();
        onRefresh();
      }
    } catch (err) {
      console.error('Error sending reply:', err);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[#C9A227]" />
      </div>
    );
  }

  return (
    <div className="border-l border-[#E5E7EB] flex-1 flex flex-col">
      <div className="p-4 border-b border-[#E5E7EB] bg-gray-50">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-[#1A1A1A] truncate">{thread.subject || '(No subject)'}</h3>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <ChevronUp className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-xs text-[#6B7280] mt-1">{messages.length} messages</p>
      </div>
      
      <div className="flex-1 overflow-y-auto max-h-[400px]">
        {messages.map((msg, idx) => (
          <div key={msg.id} className={`p-4 border-b border-[#E5E7EB] ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="text-sm font-medium text-[#1A1A1A]">{msg.from}</p>
                <p className="text-xs text-[#6B7280]">To: {msg.to}</p>
              </div>
              <p className="text-xs text-[#9CA3AF]">{msg.date}</p>
            </div>
            <div 
              className="text-sm text-[#374151] prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: msg.body || msg.snippet }}
            />
          </div>
        ))}
      </div>
      
      <div className="p-4 border-t border-[#E5E7EB] bg-white">
        {replyOpen ? (
          <div className="space-y-3">
            <Textarea
              placeholder="Write your reply..."
              value={replyBody}
              onChange={(e) => setReplyBody(e.target.value)}
              rows={4}
              className="resize-none"
            />
            <div className="flex items-center gap-2">
              <Button 
                onClick={handleSendReply}
                disabled={sending || !replyBody.trim()}
                className="bg-[#1A1A1A] hover:bg-[#333]"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send Reply
              </Button>
              <Button variant="outline" onClick={() => setReplyOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button onClick={() => setReplyOpen(true)} className="w-full" variant="outline">
            <Send className="w-4 h-4 mr-2" />
            Reply
          </Button>
        )}
      </div>
    </div>
  );
}

export default function ContactEmailThreads({ contactId, contactEmail, contactName }) {
  const { token } = useAuth();
  const [gmailStatus, setGmailStatus] = useState(null);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedThread, setSelectedThread] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeData, setComposeData] = useState({ subject: '', body: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    checkGmailStatus();
  }, []);

  useEffect(() => {
    if (gmailStatus?.connected && contactEmail) {
      fetchThreads();
    }
  }, [gmailStatus, contactEmail]);

  const checkGmailStatus = async () => {
    try {
      const res = await fetch(`${API}/api/gmail/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setGmailStatus(data);
    } catch (err) {
      console.error('Error checking Gmail status:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchThreads = async () => {
    try {
      const res = await fetch(`${API}/api/gmail/contact/${contactId}/threads`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setThreads(data.threads || []);
    } catch (err) {
      console.error('Error fetching threads:', err);
    }
  };

  const handleSendNew = async () => {
    if (!composeData.subject.trim() || !composeData.body.trim()) return;
    
    setSending(true);
    try {
      const res = await fetch(`${API}/api/gmail/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          to: contactEmail,
          subject: composeData.subject,
          body: composeData.body,
          contact_id: contactId
        })
      });
      
      if (res.ok) {
        setComposeData({ subject: '', body: '' });
        setComposeOpen(false);
        fetchThreads();
      }
    } catch (err) {
      console.error('Error sending email:', err);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[#C9A227]" />
      </div>
    );
  }

  if (!gmailStatus?.connected) {
    return (
      <Card className="border-[#E5E7EB]">
        <CardContent className="py-12">
          <div className="text-center">
            <Mail className="w-12 h-12 text-[#9CA3AF] mx-auto mb-4" />
            <h3 className="font-medium text-[#1A1A1A] mb-2">Gmail Not Connected</h3>
            <p className="text-sm text-[#6B7280] mb-4">
              Connect your Gmail to view email conversations with this contact.
            </p>
            <Link to="/settings/email">
              <Button className="bg-[#1A1A1A] hover:bg-[#333]">
                <ExternalLink className="w-4 h-4 mr-2" />
                Connect Gmail
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!contactEmail) {
    return (
      <Card className="border-[#E5E7EB]">
        <CardContent className="py-12">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h3 className="font-medium text-[#1A1A1A] mb-2">No Email Address</h3>
            <p className="text-sm text-[#6B7280]">
              This contact doesn't have an email address. Add one to view email threads.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-[#E5E7EB]">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-medium flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#C9A227]" />
            Email Threads
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchThreads}
              data-testid="refresh-threads-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button 
              size="sm" 
              onClick={() => setComposeOpen(!composeOpen)}
              className="bg-[#1A1A1A] hover:bg-[#333]"
              data-testid="compose-email-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Compose
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="p-0">
        {composeOpen && (
          <div className="p-4 border-b border-[#E5E7EB] bg-[#FDF8E8]">
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-[#6B7280]">To</label>
                <p className="text-sm text-[#1A1A1A]">{contactEmail}</p>
              </div>
              <Input
                placeholder="Subject"
                value={composeData.subject}
                onChange={(e) => setComposeData(d => ({ ...d, subject: e.target.value }))}
                data-testid="compose-subject"
              />
              <Textarea
                placeholder="Write your message..."
                value={composeData.body}
                onChange={(e) => setComposeData(d => ({ ...d, body: e.target.value }))}
                rows={4}
                data-testid="compose-body"
              />
              <div className="flex items-center gap-2">
                <Button 
                  onClick={handleSendNew}
                  disabled={sending || !composeData.subject.trim() || !composeData.body.trim()}
                  className="bg-[#C9A227] hover:bg-[#B8922A]"
                  data-testid="send-email-btn"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Send
                </Button>
                <Button variant="outline" onClick={() => setComposeOpen(false)}>
                  Cancel
                </Button>
              </div>
              <p className="text-xs text-[#9CA3AF]">Your signature will be automatically appended.</p>
            </div>
          </div>
        )}
        
        <div className="flex">
          <div className={`${selectedThread ? 'w-1/2' : 'w-full'} max-h-[500px] overflow-y-auto`}>
            {threads.length === 0 ? (
              <div className="p-8 text-center">
                <Clock className="w-8 h-8 text-[#9CA3AF] mx-auto mb-3" />
                <p className="text-sm text-[#6B7280]">No email threads with this contact yet.</p>
                <p className="text-xs text-[#9CA3AF] mt-1">Send an email to start a conversation.</p>
              </div>
            ) : (
              threads.map(thread => (
                <EmailThread 
                  key={thread.id} 
                  thread={thread}
                  onSelect={setSelectedThread}
                  isSelected={selectedThread?.id === thread.id}
                />
              ))
            )}
          </div>
          
          {selectedThread && (
            <ThreadDetail 
              thread={selectedThread}
              onClose={() => setSelectedThread(null)}
              onRefresh={fetchThreads}
              contactId={contactId}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}
